// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.SolidGaugeOptionsBuilder.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import HighchartOptionsBuilder from 'share/components/charting/highcharts/Izenda.HighchartOptionsBuilder';
import HighchartUtils from 'share/components/charting/highcharts/Izenda.HighchartUtils';
import jsFormatService from 'share/Izenda.JsFormatService';
import reportPartElementUtils from 'share/utils/Izenda.ReportPartElementUtils';
import { get, head, flow } from 'lodash/fp';
import { exportClass } from 'share/Izenda.ServiceProvider';
import ReportPartUtils from 'share/utils/Izenda.ReportPartUtils';

const TICK_COLOR = '#f2f2f2';

@exportClass('SolidGaugeOptionsBuilder')
export default class SolidGaugeOptionsBuilder extends HighchartOptionsBuilder {
  constructor(...baseArgs) {
    super(...baseArgs);
  }

  /**
   * Build Highchart variable options for each chart type
   * @param {string} visualType Highchart chart type
   * @param {Object} highchartOptions Highchart options
   * @param {Object} chartData Chart data
   */
  buildOptionsByType(visualType: string, userOptions, dataParser): Object {
    const izendaSeriesConfig = userOptions.izendaSeriesConfig || {};
    const izendaOptions = userOptions.izendaOptions;

    const min = Math.floor(izendaOptions.scaleFrom);
    const max = Math.ceil(izendaOptions.scaleTo);

    const serieName = izendaOptions.serieName;
    const plotBands = izendaOptions.threshold ? getPlotBands(izendaOptions.threshold, min, max) : [];
    const valueData = get('[0].data[0].y', userOptions.series);

    userOptions.yAxis = {};

    return {
      legend: {
        enabled: false
      },
      credits: {
        enabled: false
      },
      tooltip: {
        enabled: false
      },
      chart: {
        events: {
          click: HighchartUtils.onDrilldownGauge(userOptions, head(userOptions.series))
        }
      },
      title: {
        text: get('showLabels', izendaOptions) ? serieName : '',
        verticalAlign: 'top',
        floating: true,
        style: {
          fontSize: '13px'
        }
      },
      plotOptions: {
        series: {
          events: {
            click: undefined
          },
          dataLabels: {
            crop: false,
            overflow: 'none',
            enabled: true,
            useHTML: true,
            y: 0,
            borderWidth: 0,
            formatter: getFormatData
          },
          zones: getZones(plotBands, valueData)
        }
      },

      yAxis: {
        title: {
          enabled: false
        },
        min,
        max,

        labels: {
          formatter: getFormatLabel
        },
        plotBands,
        tickLength: 0,
        tickWidth: 0,
        tickColor: TICK_COLOR,
        gridLineColor: TICK_COLOR,
        gridLineWidth: 0,
        minorTickInterval: 0,
        minorTickWidth: 0,
        minorGridLineWidth: 0
      }
    };

    function getFormatLabel() {
      let val = this.value;

      if (val === min) {
        val = izendaOptions.scaleFrom;
      } else if (val === max) {
        val = izendaOptions.scaleTo;
      }

      return getDataFormat(val, false);
    }

    function getFormatData() {
      return getDataFormat(this.value || this.y, true);
    }

    function getDataFormat(value, isApplyAlternativeText) {
      const record = flow(
        head,
        get('data'),
        head,
        get('record')
      )(userOptions.series);
      const element = flow(
        head,
        get('yAxisField')
      )(userOptions.series);

      const fieldValues = ReportPartUtils.getFieldValue(element.columnName, record);
      const jsFormatString = get('reportPartElm.properties.dataFormattings.format.jsFormatString', element);
      const jsFormatId = get('reportPartElm.properties.dataFormattings.format.formatId', element);
      fieldValues.value = jsFormatString ? jsFormatService.format(jsFormatId, value) : value;

      return dataParser.getFormatData(
              fieldValues,
              izendaSeriesConfig[serieName],
              {
                record,
                element
              },
              isApplyAlternativeText
            );
    }

    function getPlotBands(thresholds, min, max) {
      // convert from izenda form to highchart-form
      return thresholds ? thresholds : [];
    }

    function getZones(thresholds, valueData) {
      const zones = thresholds.map(threshold => ({
        value: threshold.to,
        color: threshold.color
      }));

      if (!zones.length) {
        return zones;
      }

      // highcharts trick- for zone, data range [a,b); [b,c)
      // for cases valueData = zone[last][value] >> need to be plus 1
      zones[zones.length - 1].value = Math.max(zones[zones.length - 1].value, max, valueData) + 1;
      return zones;
    }
  }
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/ReportPartType/ReportPartGauge/Izenda.SolidGaugeOptionsBuilder.js